import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import {
    getProductListDataViewProperty,
    isLoadMore,
} from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { FormFieldStyle } from "@insite/mobius/FormField";
import { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { GridContainer, GridItem } from "@insite/mobius/index";
import Pagination, { PaginationPresentationProps, PaginationStyle } from "@insite/mobius/Pagination";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    hidePagination: isLoadMore(state),
    pagination: getProductListDataViewProperty(state, "pagination"),
});

const mapDispatchToProps = {
    addProductFilters,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductListPaginationStyles {
    pagination?: PaginationPresentationProps;
    container?: GridContainerProps;
    leftSideColumn?: GridItemProps;
    rightSideColumn?: GridItemProps;
}

export const paginationStyles: ProductListPaginationStyles = {
    container: {
        css: css`
            border: 1px solid ${getColor("common.border")};
            margin: 10px 0;
            > ${GridOffset} {
                width: 100%;
                margin: 0;
            }
        `,
    },
    leftSideColumn: {
        width: [12, 5, 5, 5, 5],
        css: css`
            display: flex;
            justify-content: start;
            align-items: center;
            padding-left: 25px;
            ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            justify-content: center;
                            padding-left: 0px;
                            > div > div[class^="Wrapper"] > ${FormFieldStyle} {
                                display: flex;
                                justify-content: center;
                            }
                        `,
                    ],
                    "max",
                )};
        `,
    },
    rightSideColumn: {
        width: [12, 7, 7, 7, 7],
        css: css`
            display: flex;
            justify-content: end;
            ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            justify-content: center;
                        `,
                    ],
                    "max",
                )};
            > div > ${PaginationStyle} {
                border: none;
            }
        `,
    },
};

const styles = paginationStyles;

const ProductListPagination = ({ addProductFilters, hidePagination, pagination, id }: Props) => {
    const [shouldScrollToTop, setShouldScrollToTop] = useState(false);
    useEffect(() => {
        if (shouldScrollToTop) {
            window.scrollTo(0, 0);
            setShouldScrollToTop(false);
        }
    }, [pagination?.page]);

    const changePage = (newPageIndex: number) => {
        addProductFilters({ page: newPageIndex });
        setShouldScrollToTop(true);
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        addProductFilters({ pageSize: newPageSize });
    };

    if (hidePagination || !pagination || !pagination.totalItemCount) {
        return null;
    }

    const { totalItemCount, pageSize } = pagination;

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.leftSideColumn}>
                <StyledWrapper>
                    <Zone contentId={id} zoneName="ProductListPagination" />
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.rightSideColumn}>
                <StyledWrapper>
                    {!(totalItemCount <= pageSize) ? (
                        <Pagination
                            {...styles.pagination}
                            resultsCount={pagination.totalItemCount}
                            currentPage={pagination.page}
                            resultsPerPage={pagination.pageSize}
                            resultsPerPageOptions={pagination.pageSizeOptions}
                            onChangePage={changePage}
                            onChangeResultsPerPage={changeResultsPerPage}
                            pageSizeCookie="ProductList-PageSize"
                            data-test-selector="productList_pagination"
                            prevPage={pagination.prevPageUri}
                            nextPage={pagination.nextPageUri}
                        />
                    ) : null}
                </StyledWrapper>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListPagination),
    definition: {
        group: "Product List",
        displayName: "Pagination",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;